import { apmBase } from '@elastic/apm-rum';
import EventEmitter from 'eventemitter3';
import { storeToRefs } from 'pinia';
import type { App } from 'vue';
import { nextTick } from 'vue';
import type { Router } from 'vue-router';
import { getUserTGroup } from './utils/utils';
import { LanguageType } from '@/interface/enum';
import Api from '@/libraries/api';
import { registerApmFilters, registerVueErrorContext } from '@/libraries/apmFilters';
import { betSlipStorageKeysToClear } from '@/libraries/betSlip/betSlipStorageKeys';
import { setI18nLanguage, setLangFromCookie } from '@/libraries/i18n';
import { getSportsApiAsiUrl, getSportsApiBsiUrl } from '@/libraries/url';
import { useAccountStore } from '@/stores/accountStore';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';
import { useHeaderDisplayStore } from '@/stores/headerDisplayStore';

window.eventEmitter = window.eventEmitter || new EventEmitter();

export async function initCustomerInfo() {
    const customerInfo = await Api.getCustomerInfo();
    if (customerInfo.abTestGroup) {
        window.userTGroup = getUserTGroup(customerInfo.abTestGroup);
    }
    return customerInfo;
}

export async function mockApi() {
    if (__MOCK_SPORTS_API_ENABLED__) {
        const { createWorker } = await import('../mock/handlers');
        const worker = createWorker();
        if (!worker) return;

        const { mockPollux } = await import('../mock/pollux-api');
        mockPollux(worker);

        await worker.start({
            onUnhandledRequest(request) {
                const url = request.url.href;
                const isPolluxRequestUnhandled = url.startsWith(getSportsApiBsiUrl()) || url.startsWith(getSportsApiAsiUrl());

                if (isPolluxRequestUnhandled) {
                    console.warn(`[MSW] Warning: captured a query without a matching request handler\n${url}`);
                }
            },
        });
    }
}

export function addSafariBackPageListener() {
    const callback = async () => {
        const navigation = window.performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
        const isByHistoryBackForward = navigation?.type === 'back_forward';

        if (isByHistoryBackForward) {
            setLangFromCookie();
            const [featureToggle, customerInfo] = await Promise.all([
                Api.getConfiguration(),
                initCustomerInfo(),
            ]);

            useFeatureToggleStore().setFeatureToggle(featureToggle);
            useAccountStore().updateCustomerInfo(customerInfo);
        }
    };
    window.addEventListener('popstate', callback, false);
}

export async function changeLanguage(language: string) {
    await setI18nLanguage(LanguageType.fromPlatform(language));
    await nextTick();
    const { setState } = useHeaderDisplayStore();
    setState({
        ppHeight: document.querySelector('.sboMenu-header')?.clientHeight ?? 0,
    });
}

export async function initApm(app: App, router: Router, oddsApiUrl: string, sampleRate: number, serverUrl: string) {
    const { ApmVuePlugin } = await import('@elastic/apm-rum-vue');
    const { getApmConfig } = await import('@/libraries/apm');

    app.use(ApmVuePlugin, {
        router,
        config: getApmConfig(oddsApiUrl, sampleRate, serverUrl),
        captureErrors: false,
    });

    registerApmFilters();
    registerVueErrorContext(app, router);

    const { customerInfo } = storeToRefs(useAccountStore());
    apmBase.setUserContext({ id: customerInfo.value.trackingId });

    const MIN_IDLE_DURATION = 2;
    const MAX_IDLE_DURATION = 60;
    const PRECISE_IDLE_LOG_DURATION = 10;
    const IMPRECISE_IDLE_LOG_INTERVAL = 5;

    const { isIdleMonitorEnabled } = storeToRefs(useFeatureToggleStore());
    if (isIdleMonitorEnabled.value && __ENV_ASI__) {
        let idleTimer: number | null;
        let startTime: Date | null;

        const clearIdleTimer = () => {
            if (idleTimer) {
                clearInterval(idleTimer);
                idleTimer = 0;
                startTime = null;
            }
        };

        const incrementIdleTime = () => {
            if (!startTime) return;

            const currentTime = new Date();

            const durationInMs = currentTime.getTime() - startTime.getTime();
            const durationInMinutes = Math.floor(durationInMs / 60_000);

            if (durationInMinutes >= MIN_IDLE_DURATION && durationInMinutes <= MAX_IDLE_DURATION) {
                if (durationInMinutes <= PRECISE_IDLE_LOG_DURATION || durationInMinutes % IMPRECISE_IDLE_LOG_INTERVAL === 0) {
                    const error = new Error(`User was idle for ${durationInMinutes} minute(s)`);
                    apmBase.captureError(error);
                }
            }

            if (durationInMinutes > MAX_IDLE_DURATION) {
                clearIdleTimer();
            }
        };

        const startIdleTimer = () => {
            startTime = new Date();
            idleTimer = window.setInterval(incrementIdleTime, 60_000);
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                startIdleTimer();
            } else {
                clearIdleTimer();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
    }
}

export function logout() {
    betSlipStorageKeysToClear.forEach(key => window.localStorage.removeItem(key));
    Api.logout();
}

export function ppReady() {
    window.isPpReady = true;

    let getPpHeightAttempt = 0;
    function setPpHeight() {
        const height = document.querySelector('.sboMenu-header')?.clientHeight;

        if (height) {
            useHeaderDisplayStore().setState({
                ppHeight: height,
            });
        }

        if (getPpHeightAttempt < 20) {
            getPpHeightAttempt++;
            setTimeout(setPpHeight, 500);
        }
    }

    if (typeof MutationObserver !== 'undefined') {
        const observer = new MutationObserver(() => {
            setPpHeight();
        });
        const target = document.querySelector('#pp-component');
        if (target === null) return;

        observer.observe(target, {
            childList: true,
        });
    }
    setPpHeight();
}

export async function updateCustomerSetting() {
    const response = await Api.UpdateCustomerSetting();
    useAccountStore().updateCustomerInfo(response);
}
